import { useState, useEffect } from "react";

export interface TestUser {
  id: string;
  email: string;
  role: "admin" | "rt" | "tv" | "parceiro";
  password: string;
  empresa?: string;
  empresaId?: string;
  vistoriadora?: string;
}

const STORAGE_KEY = "remonta_test_users";

export function useTestUsers() {
  const [users, setUsers] = useState<TestUser[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setUsers(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse test users", e);
      }
    }
    setLoaded(true);
  }, []);

  const saveUsers = (newUsers: TestUser[]) => {
    setUsers(newUsers);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newUsers));
  };

  const addUser = (user: Omit<TestUser, "id">) => {
    const newUsers = [...users, { ...user, id: crypto.randomUUID() }];
    saveUsers(newUsers);
  };

  const addUsers = (newUsersToAdd: Omit<TestUser, "id">[]) => {
    const usersWithIds = newUsersToAdd.map((user) => ({
      ...user,
      id: crypto.randomUUID(),
    }));
    const merged = [...users, ...usersWithIds];
    saveUsers(merged);
  };

  const deleteUser = (id: string) => {
    const newUsers = users.filter((u) => u.id !== id);
    saveUsers(newUsers);
  };

  const updateUser = (id: string, updates: Partial<TestUser>) => {
    const newUsers = users.map((u) => (u.id === id ? { ...u, ...updates } : u));
    saveUsers(newUsers);
  };

  return {
    users,
    loaded,
    addUser,
    addUsers,
    deleteUser,
    updateUser,
  };
}
